import * as THREE from 'three';
import {
  applyProcEnemyPose,
  buildProcEnemy,
  cloneEnemyFace,
  createProcEnemyKit,
  DEFAULT_LOOK,
  tickEnemyBlink,
  type EnemyFace,
  type ProcAnimEnemy,
} from '../enemies/ProcEnemy';
import type { PlayerController } from './PlayerController';
import type { Viewmodel } from './Viewmodel';
import { isInBathroom } from '../world/FacilityArena';
import { dirtyMirrors } from '../world/FacilityDressing';
import {
  CUFF_BACK_Z,
  HOLD_DROP,
  HOLD_FORWARD_ADS,
  HOLD_FORWARD_HIP,
  HOLD_SIDE,
  findClonedHand,
  hideBodyArms,
  placeShoulder,
  placeUpperArm,
} from './FpsHoldBridges';

/** Stand a body-scale copy in front of you so the hold is visible without a reflector. */
export type PlayerAvatarOpts = { previewInFront?: boolean };
export const AVATAR_PREVIEW_DIST = 2.2;

const _holdPos = new THREE.Vector3();
const _holdQuat = new THREE.Quaternion();
const _shoulderW = new THREE.Vector3();
const _chestW = new THREE.Vector3();
const _cuffW = new THREE.Vector3();

function normalizeCharacter(model: THREE.Object3D): void {
  model.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(model);
  const size = box.getSize(new THREE.Vector3());
  const targetH = 1.78;
  if (size.y > 0.1) {
    model.scale.multiplyScalar(targetH / size.y);
    model.updateMatrixWorld(true);
  }
  const box2 = new THREE.Box3().setFromObject(model);
  model.position.y -= box2.min.y;
}

function disposeHold(hold: THREE.Object3D): void {
  hold.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mat = mesh.material;
    if (mat instanceof THREE.MeshBasicMaterial) mat.dispose();
  });
  hold.removeFromParent();
}

/** Third-person agent body, hidden in first person and revealed only in bathroom mirrors. */
export class PlayerAvatar {
  readonly root: THREE.Group;
  private readonly model: THREE.Object3D;
  private readonly anim: ProcAnimEnemy;
  private readonly face: EnemyFace | null;
  private readonly modelBaseY: number;
  private readonly eye = new THREE.Group();
  private readonly upperArmR: THREE.Mesh;
  private readonly upperArmL: THREE.Mesh;
  private readonly shoulderR: THREE.Mesh;
  private readonly shoulderL: THREE.Mesh;
  private fpsHold: THREE.Group | null = null;
  private selfHold: THREE.Group | null = null;
  private readonly selfRoot = new THREE.Group();
  previewInFront: boolean;
  /** Calib: force the kneeling fire stance regardless of player crouch. */
  previewKneel = false;
  /** World-space copy of the FPS hold at your eyes (side/top compare). */
  showSelfHold = false;

  constructor(scene: THREE.Scene, opts?: PlayerAvatarOpts) {
    this.previewInFront = opts?.previewInFront ?? false;
    const kit = createProcEnemyKit();
    this.model = buildProcEnemy('agent', kit, false, 'pistol');
    normalizeCharacter(this.model);
    this.modelBaseY = this.model.position.y;

    this.root = new THREE.Group();
    this.root.name = 'playerAvatar';
    this.eye.name = 'avatarEye';
    this.root.add(this.model);
    this.root.add(this.eye);
    const sleeve = new THREE.MeshBasicMaterial({ color: 0x121214, fog: true });
    const limbGeo = new THREE.CylinderGeometry(0.044, 0.07, 1, 8);
    const shoulderGeo = new THREE.SphereGeometry(0.078, 10, 8);
    this.upperArmR = new THREE.Mesh(limbGeo, sleeve);
    this.upperArmL = new THREE.Mesh(limbGeo, sleeve);
    this.shoulderR = new THREE.Mesh(shoulderGeo, sleeve);
    this.shoulderL = new THREE.Mesh(shoulderGeo, sleeve);
    this.upperArmR.name = 'avatarUpperArmR';
    this.upperArmL.name = 'avatarUpperArmL';
    this.shoulderR.name = 'avatarShoulderR';
    this.shoulderL.name = 'avatarShoulderL';
    for (const m of [this.upperArmR, this.upperArmL, this.shoulderR, this.shoulderL]) {
      m.frustumCulled = false;
      m.castShadow = false;
      this.root.add(m);
    }
    this.root.visible = false;
    this.selfRoot.name = 'avatarSelfHold';
    this.selfRoot.visible = false;
    this.selfRoot.traverse((o) => {
      o.userData.ignoreHit = true;
      o.userData.noHit = true;
    });
    scene.add(this.selfRoot);
    this.root.traverse((o) => {
      o.userData.ignoreHit = true;
      o.userData.noHit = true;
      o.layers.set(2);
    });
    scene.add(this.root);

    this.face = cloneEnemyFace(this.model, DEFAULT_LOOK);
    this.anim = {
      model: this.model,
      kind: 'pistol',
      currentClip: 'idle',
      layerMode: 'full',
      upperClip: 'idle',
      lowerClip: 'idle',
      animT: 0,
      attackPhase: 'none',
      attackLockT: 0,
      lookScan: 1,
      kneelBlend: 0,
      kneelTarget: 0,
    };

    this.hookMirrors(scene);
  }

  update(player: PlayerController, dt: number, viewmodel: Viewmodel): void {
    this.root.rotation.order = 'YXZ';
    this.root.rotation.z = -player.lean * 0.12;
    if (this.previewInFront) {
      const fx = -Math.sin(player.yaw);
      const fz = -Math.cos(player.yaw);
      this.root.position.set(
        player.body.position.x + fx * AVATAR_PREVIEW_DIST,
        player.body.position.y,
        player.body.position.z + fz * AVATAR_PREVIEW_DIST,
      );
      this.root.rotation.y = player.yaw + Math.PI;
      this.root.visible = true;
    } else {
      this.root.position.copy(player.body.position);
      this.root.rotation.y = player.yaw;
      this.root.visible = false;
    }

    if (!this.previewInFront && !this.showSelfHold && !facingBathMirrors(player)) return;

    const crouch = Math.max(player.crouchBlend, player.slideBlend);
    this.anim.kneelTarget = this.previewKneel ? 1 : crouch;

    const spd = Math.hypot(player.body.velocity.x, player.body.velocity.z);
    const moving = !this.previewKneel && crouch < 0.2 && player.body.grounded && spd > 0.45 && !player.isSliding;
    const run = moving && spd > 7.2;
    const loco = moving ? (run ? 'run' : 'walk') : 'idle';

    const aiming = viewmodel.adsBlend > 0.2;
    this.anim.layerMode = 'aimloco';
    this.anim.upperClip = 'lookaround';
    this.anim.lowerClip = loco;
    this.anim.currentClip = loco;
    this.anim.lockHead = aiming;
    tickEnemyBlink(this.face, dt);

    if (moving) this.anim.animT = player.bobPhase;
    applyProcEnemyPose(this.anim, dt);
    if (moving) this.anim.animT = player.bobPhase;

    const kb = this.anim.kneelBlend ?? 0;
    this.model.position.y = this.modelBaseY + kb * 0.015 - player.slideBlend * 0.1;

    const bones = this.model.userData.procBones as Record<string, THREE.Object3D> | undefined;
    hideBodyArms(bones, true);

    const look = this.previewInFront ? player.pitch : -player.pitch;
    const ads = viewmodel.adsBlend;
    const fists = viewmodel.currentWeapon.id === 'punch';
    const holdFwd = fists
      ? THREE.MathUtils.lerp(-0.05, 0.0, ads)
      : THREE.MathUtils.lerp(HOLD_FORWARD_HIP, HOLD_FORWARD_ADS, ads);
    const holdDrop = fists ? HOLD_DROP - 0.08 : HOLD_DROP;
    const eyeDrop = 0.34 * kb;
    this.eye.position.set(HOLD_SIDE, 1.62 - holdDrop - eyeDrop, -holdFwd);
    this.eye.rotation.order = 'YXZ';
    this.eye.rotation.set(look, 0, 0);
    this.stampFpsHold(viewmodel);
    this.stampSelfHold(player, viewmodel);
    this.updateUpperArms(bones);
    if (bones) {
      bones.Neck?.rotateX(look * 0.22);
      bones.Head?.rotateX(look * 0.55);
    }
  }

  private stampFpsHold(viewmodel: Viewmodel): void {
    if (!this.fpsHold || !viewmodel.holdTreeMatches(this.fpsHold)) {
      if (this.fpsHold) disposeHold(this.fpsHold);
      this.fpsHold = viewmodel.cloneHoldVisual();
      this.eye.add(this.fpsHold);
      this.fpsHold.traverse((o) => o.layers.set(2));
      dirtyMirrors();
    }
    viewmodel.syncHoldVisual(this.fpsHold);
    viewmodel.readHoldInCamera(_holdPos, _holdQuat);
    this.fpsHold.position.copy(_holdPos);
    this.fpsHold.quaternion.copy(_holdQuat);
  }

  private stampSelfHold(player: PlayerController, viewmodel: Viewmodel): void {
    if (!this.showSelfHold) {
      this.selfRoot.visible = false;
      return;
    }
    this.selfRoot.visible = true;
    const eyeH = player.body.position.y + player.body.height - 0.12;
    this.selfRoot.position.set(player.body.position.x, eyeH, player.body.position.z);
    this.selfRoot.rotation.order = 'YXZ';
    this.selfRoot.rotation.set(player.pitch, player.yaw, 0);
    if (!this.selfHold || !viewmodel.holdTreeMatches(this.selfHold)) {
      if (this.selfHold) disposeHold(this.selfHold);
      this.selfHold = viewmodel.cloneHoldVisual();
      this.selfRoot.add(this.selfHold);
    }
    viewmodel.syncHoldVisual(this.selfHold);
    viewmodel.readHoldInCamera(_holdPos, _holdQuat);
    this.selfHold.position.copy(_holdPos);
    this.selfHold.quaternion.copy(_holdQuat);
  }

  private updateUpperArms(bones: Record<string, THREE.Object3D> | undefined): void {
    const hold = this.fpsHold;
    if (!hold || !bones) {
      this.upperArmR.visible = false;
      this.upperArmL.visible = false;
      this.shoulderR.visible = false;
      this.shoulderL.visible = false;
      return;
    }
    this.root.updateWorldMatrix(true, false);
    hold.updateWorldMatrix(true, true);
    bones.Spine2?.updateWorldMatrix(true, false);
    const handR = findClonedHand(hold, true);
    const handL = findClonedHand(hold, false);
    if (handR?.visible && bones.RightArm) {
      bones.RightArm.getWorldPosition(_shoulderW);
      if (bones.Spine2) {
        bones.Spine2.getWorldPosition(_chestW);
        _shoulderW.lerp(_chestW, 0.28);
      }
      placeShoulder(this.shoulderR, this.root, _shoulderW);
      handR.localToWorld(_cuffW.set(0, 0, CUFF_BACK_Z));
      placeUpperArm(this.upperArmR, this.root, _shoulderW, _cuffW);
    } else {
      this.upperArmR.visible = false;
      this.shoulderR.visible = false;
    }
    if (handL?.visible && bones.LeftArm) {
      bones.LeftArm.getWorldPosition(_shoulderW);
      if (bones.Spine2) {
        bones.Spine2.getWorldPosition(_chestW);
        _shoulderW.lerp(_chestW, 0.28);
      }
      placeShoulder(this.shoulderL, this.root, _shoulderW);
      handL.localToWorld(_cuffW.set(0, 0, CUFF_BACK_Z));
      placeUpperArm(this.upperArmL, this.root, _shoulderW, _cuffW);
    } else {
      this.upperArmL.visible = false;
      this.shoulderL.visible = false;
    }
  }

  private hookMirrors(scene: THREE.Scene): void {
    scene.traverse((o) => {
      if (o.name !== 'bath_mirror') return;
      const prev = o.onBeforeRender.bind(o);
      o.onBeforeRender = (renderer, scn, camera, geometry, material, group) => {
        this.root.visible = true;
        camera.layers.enable(2);
        prev(renderer, scn, camera, geometry, material, group);
        camera.layers.disable(2);
        if (!this.previewInFront) this.root.visible = false;
      };
    });
  }
}

const _faceFwd = new THREE.Vector3();

/** In the WC and looking toward the sink-wall mirrors. */
function facingBathMirrors(player: PlayerController): boolean {
  const p = player.body.position;
  const inBath = isInBathroom(p) || (p.x > -5.1 && p.x < 5.1 && p.z > -0.1 && p.z < 11.8 && p.y > -0.3 && p.y < 2.85);
  if (!inBath) return false;
  player.camera.getWorldDirection(_faceFwd);
  return _faceFwd.x > 0.2;
}
